import { useCallback, useState } from 'react';
import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Button, cn, CloseIcon, Input, PlusIcon } from '@playerbase/ui';
import type { GameDevStage, SemanticTier, StageColour } from '@playerbase/types';

import {
  countBuildMarkersForStage,
  createDevStage,
  deleteUnusedDevStage,
  removeDevStageWithMigration,
  reorderDevStages,
  updateDevStage,
} from '../../../games/dev-stages-data.js';
import { useShell } from '../../../shell/use-shell.js';
import { ColourPalettePopover } from './colour-palette-popover.js';
import { TierPickerPopover } from './tier-picker-popover.js';
import { DevStageRemoveDialog } from './dev-stage-remove-dialog.js';

type EditableLabelProps = {
  value: string;
  onCommit: (next: string) => void;
};

function EditableLabel({ value, onCommit }: EditableLabelProps): JSX.Element {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);

  if (!editing) {
    return (
      <button
        type="button"
        onClick={() => {
          setDraft(value);
          setEditing(true);
        }}
        className="rounded-sm px-1 text-left text-sm text-text hover:bg-surface-2"
      >
        {value}
      </button>
    );
  }

  return (
    <Input
      autoFocus
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={() => {
        const trimmed = draft.trim();
        if (trimmed.length > 0 && trimmed !== value) onCommit(trimmed);
        setEditing(false);
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          (e.target as HTMLInputElement).blur();
        } else if (e.key === 'Escape') {
          setDraft(value);
          setEditing(false);
        }
      }}
      className="h-7 w-40 text-sm"
    />
  );
}

type SortableStageRowProps = {
  stage: GameDevStage;
  isLast: boolean;
  onColourChange: (id: string, colour: StageColour) => void;
  onTierChange: (id: string, tier: SemanticTier) => void;
  onLabelChange: (id: string, label: string) => void;
  onRemove: (stage: GameDevStage) => void;
};

function SortableStageRow({
  stage,
  isLast,
  onColourChange,
  onTierChange,
  onLabelChange,
  onRemove,
}: SortableStageRowProps): JSX.Element {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: stage.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <li
      ref={setNodeRef}
      style={style}
      className={cn(
        'flex items-center gap-3 border-b border-border px-3 py-2 last:border-b-0',
        isDragging && 'relative z-10 bg-surface-2 shadow-md',
      )}
    >
      {/* Drag handle is the only draggable surface. The rest of the row keeps
          its native interactions (popover triggers, inline edit, remove). */}
      <button
        type="button"
        aria-label={`Reorder ${stage.label}`}
        className="cursor-grab select-none rounded-sm px-1 text-text-muted hover:bg-surface-2 active:cursor-grabbing"
        {...attributes}
        {...listeners}
      >
        ⋮⋮
      </button>
      <ColourPalettePopover
        value={stage.colour}
        onChange={(c) => onColourChange(stage.id, c)}
      />
      <div className="flex-1">
        <EditableLabel value={stage.label} onCommit={(next) => onLabelChange(stage.id, next)} />
      </div>
      <TierPickerPopover
        value={stage.semanticTier}
        onChange={(t) => onTierChange(stage.id, t)}
      />
      <button
        type="button"
        disabled={isLast}
        onClick={() => onRemove(stage)}
        title={isLast ? 'You need at least one stage' : 'Remove stage'}
        className={cn(
          'rounded-sm p-1 text-text-muted transition-colors',
          isLast
            ? 'cursor-not-allowed opacity-40'
            : 'hover:bg-surface-2 hover:text-text',
        )}
        aria-label={`Remove ${stage.label}`}
      >
        <CloseIcon />
      </button>
    </li>
  );
}

export function DevStagesSection(): JSX.Element | null {
  const { activeGame, devStages, refreshDevStages } = useShell();

  const [removeTarget, setRemoveTarget] = useState<GameDevStage | null>(null);
  const [removeBusy, setRemoveBusy] = useState(false);
  const [removeBuildsCount, setRemoveBuildsCount] = useState(0);

  const sensors = useSensors(
    // 4px activation distance prevents palette / tier / label clicks from
    // being misinterpreted as drags.
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleColourChange = useCallback(
    async (id: string, colour: StageColour): Promise<void> => {
      await updateDevStage(id, { colour });
      await refreshDevStages();
    },
    [refreshDevStages],
  );

  const handleTierChange = useCallback(
    async (id: string, semanticTier: SemanticTier): Promise<void> => {
      await updateDevStage(id, { semanticTier });
      await refreshDevStages();
    },
    [refreshDevStages],
  );

  const handleLabelChange = useCallback(
    async (id: string, label: string): Promise<void> => {
      await updateDevStage(id, { label });
      await refreshDevStages();
    },
    [refreshDevStages],
  );

  const handleAdd = useCallback(async (): Promise<void> => {
    if (activeGame === null) return;
    // Use max(order_index) + 1 rather than devStages.length: indices can be
    // sparse after reorders + removes, and reusing an existing index trips
    // the (game_id, order_index) unique constraint and the insert fails
    // silently.
    const nextIndex =
      devStages.length === 0
        ? 0
        : Math.max(...devStages.map((s) => s.orderIndex)) + 1;
    await createDevStage({
      gameId: activeGame.id,
      label: 'New stage',
      colour: 'gray',
      semanticTier: 'early',
      orderIndex: nextIndex,
    });
    await refreshDevStages();
  }, [activeGame, devStages, refreshDevStages]);

  const startRemove = useCallback(
    async (stage: GameDevStage): Promise<void> => {
      const count = await countBuildMarkersForStage(stage.id);
      setRemoveBuildsCount(count);
      setRemoveTarget(stage);
    },
    [],
  );

  const confirmRemove = useCallback(
    async (targetStageId: string | null): Promise<void> => {
      if (removeTarget === null) return;
      setRemoveBusy(true);
      try {
        if (targetStageId === null) {
          await deleteUnusedDevStage(removeTarget.id);
        } else {
          await removeDevStageWithMigration(removeTarget.id, targetStageId);
        }
        await refreshDevStages();
      } finally {
        setRemoveBusy(false);
        setRemoveTarget(null);
        setRemoveBuildsCount(0);
      }
    },
    [removeTarget, refreshDevStages],
  );

  const handleDragEnd = useCallback(
    async (e: DragEndEvent): Promise<void> => {
      const { active, over } = e;
      if (over === null || active.id === over.id) return;
      if (activeGame === null) return;
      const ids = devStages.map((s) => s.id);
      const oldIdx = ids.indexOf(String(active.id));
      const newIdx = ids.indexOf(String(over.id));
      if (oldIdx < 0 || newIdx < 0) return;
      const next = arrayMove(ids, oldIdx, newIdx);
      await reorderDevStages(activeGame.id, next);
      await refreshDevStages();
    },
    [activeGame, devStages, refreshDevStages],
  );

  if (activeGame === null) return null;

  const canRemoveAny = devStages.length > 1;

  return (
    <section className="mt-10 space-y-4">
      <header>
        <h2 className="font-display text-lg font-semibold text-text">
          Development stages
        </h2>
        <p className="mt-1 text-xs text-text-muted">
          These are the stages your builds can be in. Default stages are configured for you —
          customise as your team needs. The first stage in the list is the default for new builds.
        </p>
      </header>

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={(e) => void handleDragEnd(e)}
      >
        <SortableContext
          items={devStages.map((s) => s.id)}
          strategy={verticalListSortingStrategy}
        >
          <ul className="rounded-md border border-border">
            {devStages.map((stage) => (
              <SortableStageRow
                key={stage.id}
                stage={stage}
                isLast={!canRemoveAny}
                onColourChange={(id, c) => void handleColourChange(id, c)}
                onTierChange={(id, t) => void handleTierChange(id, t)}
                onLabelChange={(id, l) => void handleLabelChange(id, l)}
                onRemove={(s) => void startRemove(s)}
              />
            ))}
          </ul>
        </SortableContext>
      </DndContext>

      <div>
        <Button variant="ghost" size="sm" onClick={() => void handleAdd()}>
          <PlusIcon />
          <span>Add stage</span>
        </Button>
      </div>

      <DevStageRemoveDialog
        open={removeTarget !== null}
        stage={removeTarget}
        otherStages={devStages.filter((s) => s.id !== removeTarget?.id)}
        buildsCount={removeBuildsCount}
        busy={removeBusy}
        onCancel={() => {
          setRemoveTarget(null);
          setRemoveBuildsCount(0);
        }}
        onConfirm={(target) => void confirmRemove(target)}
      />
    </section>
  );
}
